import React, { ReactElement, useState } from 'react';
import { CallFieldMappingConfig, CallLogData, DateCallFieldMappingConfig } from '../../store/state/createJobState';
import Modal from '../Modal';
import ct from 'countries-and-timezones';
import PreviewTable, { PreviewTableData } from '../PreviewTable';
import { getCallFieldMappingResult } from '../../store/selectors/createJobSelectors';

const timezones = getTimezones();

export default function DateMappingConfigurationModal(props: Props) {
	const [timezone, setTimezone] = useState<string>(props.mappingConfig.timezone);
	const [showDateHelp, setShowDateHelp] = useState<boolean>(false);
	const tableData = getPreviewTableData();
	
	return (
		<Modal onClose={props.onCancel} renderActions={renderActions}>
			<React.Fragment>
				<h3 className="h3-mb-3">Configure <span className="text-bold">{props.mappingConfig.displayName}</span> parameters</h3>

				<div className="mb-2">Time zone:</div>
				<div className="mb-5">
					<select className="date-mapping-configuration-modal__timezone-select" value={timezone ?? ''} placeholder="Select a timezone..." onChange={e => setTimezone(e.target.value)}>
						{timezones.map((tz, i) => (
							<option value={tz} key={i}>{tz}</option>
						))}
					</select>
				</div>

				{!!tableData && <PreviewTable tableData={tableData} />}

				{!showDateHelp && <div className="mt-2"><span className="link" role="button" onClick={() => setShowDateHelp(true)}>Need help?</span></div>}
				{showDateHelp && <div className="mt-2"><span className="link" role="button" onClick={() => setShowDateHelp(false)}>Close help</span></div>}
				
				{showDateHelp &&
					<div className="mt-2">
						<h3 className="text-bold h3-mb-1">Call log dates</h3>

						<div className="mb-4">
							In order for Creovai to parse call log dates, they need to be in a format Creovai can understand.
							Many common date formats will work, but a couple examples are:
						</div>

						<div className="text-italic mb-1">MM/dd/yyyy hh:mm:ss AM/PM</div>
						<div className="mb-1">or</div>
						<div className="text-italic mb-4">MM-dd-yyyy hh:mm:ss AM/PM</div>

						<div>
							You may also use a standardized format such as ISO 8601 or RFC2822.
						</div>
					</div>
				}
				
			</React.Fragment>
		</Modal>
	);

	function renderActions(): ReactElement {
		return (
			<div className="text-right">
				<button className="btn btn_link mr-2" onClick={props.onCancel}>Cancel</button>
				<button className="btn" onClick={onDone} disabled={isDoneDisabled()}>Done</button>
			</div>
		)
	}
	
	function isDoneDisabled(): boolean {
		return !timezone;
	}
	
	function onDone(): void {
		if (isDoneDisabled()) return;
		
		props.onConfigured(getCurrentConfig());
	}
	
	function getCurrentConfig(): DateCallFieldMappingConfig {
		return {
			...props.mappingConfig,
			columnIndex: props.logColumnIndex,
			timezone
		}
	}
	
	function getPreviewTableData(): PreviewTableData | null {
		const maxRowsToRender = 100;
		const config = getCurrentConfig();
		
		const columnIndex = config.columnIndex;
		if (columnIndex == null) return null;
		
		return {
			header: props.callLogData.headers[columnIndex] ?? 'Unknown',
			rows: props.callLogData.callRows.slice(0, maxRowsToRender).map(row => {
				return {
					rawValue: row[columnIndex] ?? '',
					mappingResult: getCallFieldMappingResult(row, config)
				};
			}),
			rowsAreTruncated: props.callLogData.callRows.length > maxRowsToRender
		}
	}
}

function getTimezones(): string[] {
	const data = ct.getAllTimezones();
	if (!data)
		return [];

	return Object.values(data).map(tz => tz.name).sort();
}

interface Props {
	// omitting the column index because it gets
	// passed in separately as logColumnIndex and
	// so don't want things to accidentally use it
	mappingConfig: Omit<DateCallFieldMappingConfig, 'columnIndex'>;
	logColumnIndex: number;
	callLogData: CallLogData;
	onCancel: () => void;
	onConfigured: (config: CallFieldMappingConfig) => void;
}
